import {
  ALL_MILESTONE_CANTONAL_SUPPORT,
  MILESTONE_CANTONAL_SUPPORT_TYPE,
  MILESTONE_STATUS,
  MILESTONE_TYPE,
} from 'src/shared/constants/milestone-constants'
import { ScreenLayout } from 'src/shared/layout/ScreenLayout'
import { useParams } from 'react-router'
import { useNotificationService } from 'src/shared/utils/NotificationService'
import { useDelayedNavigate } from 'src/shared/utils/hooks/navigation-hooks'
import { useClient } from 'urql'
import { Utils } from 'src/shared/utils/Utils'
import { useMessageSource } from 'src/i18n/useMessageSource'
import { SaveAndBackButton, SaveButton } from 'src/shared/button/Buttons'
import { PageLayout } from 'src/shared/layout/PageLayout'
import { HelpAndInstructions } from 'src/shared/presentation/HelpAndInstructions'
import { Card } from '@mui/material'
import { Form } from 'react-final-form'
import { Fragment, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { DirtyFormSpy } from 'src/shared/form/dirty/DirtyFormSpy'
import { Mutation_Root, Query_Root } from 'src/@types/graphql'
import {
  milestoneConclusionDataQuery,
  updateMilestoneCantonalSupportQuery,
} from 'src/screens/shared/implementation/details/milestoneQueries'
import { required } from 'src/shared/form/validation/validators'
import { ROUTES } from 'src/routing/routes'
import { UserContext } from 'src/user/UserContext'
import { NotAuthorized } from 'src/shared/not-authorized/NotAuthorized'
import { RadioButtonGroupField } from 'src/shared/form/control/RadioButtonGroupField'

interface Props {
  baseUrl: '/pf-kap' | '/pf-pgv'
}

type originType = 'SAVE' | 'SAVE_AND_BACK'

interface FormValues {
  selected: MILESTONE_CANTONAL_SUPPORT_TYPE | null
}

export const FinalReportConclusionCantonalSupportEditPage = ({ baseUrl }: Props) => {
  const { projectId, milestoneId } = useParams()

  const notificationService = useNotificationService()
  const navigate = useDelayedNavigate()
  const urqlClient = useClient()
  const process = Utils.resolveProcess(baseUrl)
  const milestonePath = Utils.resolveMilestonePath(MILESTONE_TYPE.FINAL_REPORT)

  const originRef = useRef<originType>('SAVE')

  const { user } = useContext(UserContext)
  const { getMessage } = useMessageSource()

  const [initialValues, setInitialValues] = useState<FormValues>()
  const [canEdit, setCanEdit] = useState<boolean>()

  const fetchData = useCallback(async () => {
    const { data } = await urqlClient
      .query<{ milestone_by_pk: Query_Root['milestone_by_pk'] }>(milestoneConclusionDataQuery, { milestoneId })
      .toPromise()

    if (data?.milestone_by_pk) {
      const cantonalSupport = (data.milestone_by_pk.cantonal_support ?? []) as MILESTONE_CANTONAL_SUPPORT_TYPE[]
      // If multiple values are selected (from outdated milestones - before single selection change), we set null to force user to select only one option
      const singleSelection = cantonalSupport.length > 1 ? null : cantonalSupport[0] ?? null

      setInitialValues({
        selected: singleSelection,
      })
      setCanEdit(data.milestone_by_pk.status !== MILESTONE_STATUS.DONE && Utils.isInternalRole(user.roles))
    }
  }, [urqlClient, milestoneId, user.roles])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const onSubmit = (origin: originType) => (_: any) => {
    // trigger submit event
    if (originRef) {
      originRef.current = origin
    }
    document.getElementById('edit-form')?.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }))
  }

  const updateHandler = async (values: FormValues) => {
    // Convert single value to array since it is saved and used like array
    const cantonalSupportArray = values.selected !== null ? [values.selected] : []

    const { data, error } = await urqlClient
      .mutation<{ update_milestone: Mutation_Root['update_milestone'] }>(updateMilestoneCantonalSupportQuery, {
        id: milestoneId,
        cantonalSupport: cantonalSupportArray,
      })
      .toPromise()

    if (error || data?.update_milestone?.affected_rows !== 1) {
      notificationService.operationFailed()
    } else {
      setInitialValues({ selected: values.selected })
      notificationService.changesSaved()
      if (originRef.current === 'SAVE_AND_BACK') {
        onBack()
      }
    }
  }

  const onBack = () => {
    navigate(ROUTES.PfKapFinalReportMilestoneRoot.nested.Conclusion.params({ projectId, milestoneId, milestonePath }))
  }

  if (canEdit === false) {
    return <NotAuthorized inSecondLevelNavigationTab />
  }

  return (
    <ScreenLayout
      title={getMessage(`label.milestone.conclusion.cantonal_support.edit`)}
      hasSecondLevelNavigation={false}
      onBack={onBack}
      actions={
        <>
          <SaveAndBackButton origin="header" onClick={onSubmit('SAVE_AND_BACK')} />
          <SaveButton origin="header" onClick={onSubmit('SAVE')} />
        </>
      }
    >
      <PageLayout>
        <HelpAndInstructions labelKey={`label.help.milestone.conclusion.cantonal_support.${process.toLowerCase()}`} />

        <Card>
          <>
            {initialValues && (
              <Form<FormValues>
                initialValues={initialValues}
                onSubmit={updateHandler}
                render={({ handleSubmit }) => {
                  return (
                    <form onSubmit={handleSubmit} id={'edit-form'}>
                      <Fragment>
                        <RadioButtonGroupField
                          required
                          label={getMessage('label.milestone.conclusion.cantonal_support')}
                          name={'selected'}
                          validate={required()}
                          data={ALL_MILESTONE_CANTONAL_SUPPORT.map((key) => {
                            return {
                              label: `${getMessage(`label.milestone.conclusion.cantonal_support.${key}`)}`,
                              value: key,
                            }
                          })}
                        />
                      </Fragment>

                      <DirtyFormSpy />
                    </form>
                  )
                }}
              />
            )}
          </>
        </Card>
      </PageLayout>
    </ScreenLayout>
  )
}
